/**
 * EL INTERRUPTOR DEL MODO SIN CONEXIÓN: `platform_settings.modo_sin_conexion`.
 *
 * Piloto por empresa, con la regla de todos los pilotos (docs/radar/interruptores.md):
 *   encendido ⇔ hay empresa ∧ enabled === true ∧ (all === true ∨ companies la contiene)
 * La lista vacía es NINGUNA. Apagado (o sin fila), la app es exactamente la de antes:
 * ni se guarda nada, ni se contesta nada desde el teléfono, ni cambia el arranque. Y al
 * apagarse se borra lo que hubiera guardado.
 *
 * El valor se recuerda en el aparato (`localStorage`, por usuario) porque hace falta
 * ANTES de saber la empresa —en el arranque, sin red—; se vuelve a mirar en la base al
 * entrar y cada pocos minutos (`SincronizacionOffline`). Si la base no contesta, vale lo
 * último que se supo.
 */
import { supabase } from "@/integrations/supabase/client";

export interface AjusteModoSinConexion {
  enabled?: unknown;
  all?: unknown;
  companies?: unknown;
}

export const CLAVE_AJUSTE = "modo_sin_conexion";

/** La regla de los pilotos (la misma que `fociEnabledFor`; la vigila interruptoresTodas.parity). */
export function modoSinConexionPara(ajuste: AjusteModoSinConexion | null | undefined, companyId: string | null | undefined): boolean {
  if (!ajuste || typeof ajuste !== "object" || !companyId) return false;
  if (ajuste.enabled !== true) return false;
  if (ajuste.all === true) return true;
  return Array.isArray(ajuste.companies) && ajuste.companies.includes(companyId);
}

const claveLocal = (uid: string) => `ulture-modo-sin-conexion:${uid}`;

/** Lo último que se supo para este usuario (false si nunca se supo). Sin red. */
export function modoGuardado(uid: string | null | undefined): boolean {
  if (!uid) return false;
  try {
    return localStorage.getItem(claveLocal(uid)) === "1";
  } catch {
    return false;
  }
}

export function recordarModo(uid: string, activo: boolean): void {
  try {
    if (activo) localStorage.setItem(claveLocal(uid), "1");
    else localStorage.removeItem(claveLocal(uid));
  } catch {
    /* sin almacenamiento: se volverá a mirar en la base */
  }
}

/**
 * Mira el interruptor en la base para esta empresa. `null` si no se pudo leer (sin
 * red): entonces manda lo último que se supo.
 */
export async function leerInterruptor(companyId: string): Promise<boolean | null> {
  try {
    const { data, error } = await supabase.from("platform_settings").select("value").eq("key", CLAVE_AJUSTE).maybeSingle();
    if (error) return null;
    return modoSinConexionPara((data as { value?: AjusteModoSinConexion } | null)?.value ?? null, companyId);
  } catch {
    return null;
  }
}
